import type { ButtonProps } from '@packages/shared';
import { Button, Link, useTrackCampaign } from '@packages/shared';
import {
  ArrowLargeLeft32,
  ArrowLargeRight32,
  ArrowMediumLeft32,
  ArrowMediumRight32,
  ArrowMiniLeft32,
  ArrowMiniRight32,
} from '@packages/themes/icons';
import { useTheme } from '@mui/material';
import { useRef } from 'react';
import type { DrupalIconTypes, Size } from '../../../interfaces';
import { ComponentTypes } from '../../../interfaces';
import { buildLinkWithArguments } from '../../utils';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import type { LinkByButtonType, Icon } from './types';

const getIcon = (icon: Icon, size: Size) => {
  const icons = {
    ArrowRight: {
      small: <ArrowMiniRight32 />,
      medium: <ArrowMediumRight32 />,
      large: <ArrowLargeRight32 />,
    },
    ArrowLeft: {
      small: <ArrowMiniLeft32 />,
      medium: <ArrowMediumLeft32 />,
      large: <ArrowLargeLeft32 />,
    },
  };

  return icons[icon][size];
};

export const LinkByButton = ({
  data,
  icon,
}: {
  data: LinkByButtonType;
  icon?: DrupalIconTypes;
}) => {
  const {
    field_link: link,
    field_text: text,
    field_layout: color,
    behavior_paragraph_layout: behaviorParagraphLayout,
    behavior_paragraph_size: behaviorSize,
    field_link_target: linkTarget,
  } = data;

  const finalColor = (behaviorParagraphLayout?.paragraph_layout || color) as ButtonProps['color'];

  const iconElement = icon && behaviorSize && getIcon(icon, behaviorSize);
  const iconPosition = icon && icon.startsWith('ArrowRight') ? 'endIcon' : 'startIcon';

  const finalLink = buildLinkWithArguments(link, data);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { trackClick } = useTrackCampaign(data, buttonRef, finalLink);
  const { palette } = useTheme();

  return (
    <Button
      ref={buttonRef}
      LinkComponent={Link}
      href={finalLink}
      // runtime-check if value is in type values
      {...(finalColor && Object.keys(palette).includes(finalColor) && { color: finalColor })}
      {...(behaviorSize && {
        size: behaviorSize,
      })}
      {...(icon && iconElement && { [iconPosition]: iconElement })}
      target={linkTarget || '_self'}
      onClick={() => trackClick()}
    >
      {text}
    </Button>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({ [ComponentTypes.LinkByButton]: LinkByButton });
